import axios from 'axios';
import type { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { NetworkError, ApiError } from './api-error';
import { GQL_API_KEY, GQL_END_POINT } from '@shared/configs';

class AxiosClient {
  private baseUrl: string;
  private apiKey: string;
  public axios: AxiosInstance;

  constructor(url: string, apiKey: string) {
    this.baseUrl = url;
    this.apiKey = apiKey;
    this.axios = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.apiKey,
      },
    });

    this.axios.interceptors.response.use(
      (response: AxiosResponse) => response.data,
      (error: AxiosError) => {
        // More robust error handling
        console.error('API Error:', error.message);
        if (error.response) {
          // Server-side error
          console.error('Response data:', error.response.data);
          console.error('Response status:', error.response.status);
          console.error('Response headers:', error.response.headers);
          return Promise.reject(
            new ApiError(error.message, error.response.status.toString())
          );
        } else if (error.request) {
          // No response received
          console.error('Request made, no response received:', error.request);
          return Promise.reject(
            new NetworkError('Network error, please try again.')
          );
        } else {
          // Other errors
          console.error('Error', error.message);
          return Promise.reject(new Error('An unexpected error occurred.'));
        }
      }
    );
  }

  public async post<TResult = unknown, TData = Record<string, unknown>>(
    body: TData
  ): Promise<TResult | undefined> {
    try {
      const response: AxiosResponse<TResult> = await this.axios.post('/', body);
      return response.data;
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw e;
      }
    }
  }

  public async gql<TResult = unknown>(
    query: string,
    variables: Record<string, unknown> = {}
  ): Promise<TResult | undefined> {
    try {
      const gqlQueryParams = {
        query,
        variables,
      };
      const response: TResult | undefined =
        await this.post<TResult>(gqlQueryParams);
      return response;
    } catch (e: unknown) {
      if (e instanceof Error) {
        throw e;
      }
    }
  }
}

export const apiClient = new AxiosClient(GQL_END_POINT, GQL_API_KEY);
